// ExpandedProductCard.mobile.jsx
// This will replace the current ExpandedProductCard.jsx with mobile-responsive implementation

import React, { useState, useMemo, memo, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import MobileProductVideo from './MobileProductVideo';
import ModalProductCard from './ModalProductCard';
import { 
  scheduleHighPriorityUpdate, 
  scheduleLowPriorityUpdate,
  cancelScheduledUpdate,
  forceLayout,
  optimizeElement,
  cleanupElementOptimizations,
  prepareElementForFocus,
  isElementInViewport
} from './PerformanceOptimizations';
import './ExpandedProductCard.scss';

const ROWS = 1;
const DESKTOP_COLS = 4;
const MOBILE_COLS = 4;
const PRODUCTS_PER_PAGE = ROWS * DESKTOP_COLS; // Default for desktop

// Memoized product card to prevent unnecessary re-renders
const MemoizedModalProductCard = memo(ModalProductCard);

// Extremely lightweight placeholder component optimized for performance
const PlaceholderCard = memo(() => (
  <div style={{ 
    width: '100%', 
    height: '100%', 
    background: '#f9f9f9', 
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    contain: 'strict', // Apply strict containment for maximum performance
    willChange: 'contents', // Optimize for content changes
    overflow: 'hidden' // Prevent any content from breaking the container
  }}>
    {/* Empty div with placeholder pulse animation - extremely lightweight */}
    <div className="placeholder-pulse" style={{ 
      width: 20, 
      height: 20, 
      background: '#eee', 
      borderRadius: '50%',
      transform: 'translateZ(0)' // Force GPU acceleration
    }} />
  </div>
), () => true); // Always return true from areEqual to prevent any re-renders

// Other Products List component
const OtherProductsList = ({ products, isMobile }) => {
  return (
    <div style={{ 
      display: 'grid', 
      gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr', 
      gap: isMobile ? 8 : 6, 
      marginTop: 8 
    }}>
      {products.map((otherProduct, index) => 
        otherProduct.id === 'placeholder' ? (
          <PlaceholderCard key={`placeholder-${index}`} />
        ) : (
          <MemoizedModalProductCard
            key={otherProduct.id}
            product={otherProduct}
            config={otherProduct.configs?.[0]}
            canonicalProduct={otherProduct.canonicalProduct}
            onSale={otherProduct.onSale}
            hasValidUrl={otherProduct.hasValidUrl}
            small={true}
            compact={isMobile}
            distance={otherProduct.distance}
            city={otherProduct.city}
            state={otherProduct.state}
            onViewDetails={otherProduct.onViewDetails}
          />
        )
      )}
    </div>
  );
};

const ExpandedProductCard = ({
  product,
  store,
  config,
  canonicalProduct,
  onSale,
  hasValidUrl,
  productUrl,
  otherProducts,
  onClose,
  distance,
  city,
  state
}) => {
  // Menu type indicators
  const isMedical = product.menuTypes?.includes('medical');
  const isRecreational = product.menuTypes?.includes('recreational');
  const productBrand = canonicalProduct?.brand || '';
  const productDisplayName = canonicalProduct?.displayName || product.displayName;
  const productTHC = canonicalProduct?.thc || '';

  // Responsive design detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Track staged loading of components to reduce initial render time
  const [loadStage, setLoadStage] = useState(0);
  const [animation, setAnimation] = useState('fadeIn');
  const cardRef = useRef(null);
  
  // Swipe functionality state
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [hasUserSwiped, setHasUserSwiped] = useState(false);
  const swipeAreaRef = useRef(null);
  const minSwipeDistance = 50; // Minimum distance for a swipe to be detected
  
  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // Optimized pagination with early bailout and minimal computation
  const [page, setPage] = useState(0);
  
  // Calculate total pages based on mobile/desktop view
  const totalPages = useMemo(() => {
    const productsPerPage = isMobile ? ROWS * MOBILE_COLS : PRODUCTS_PER_PAGE;
    return Math.ceil(otherProducts.length / productsPerPage);
  }, [otherProducts.length, isMobile] // Recalculate when array length or mobile status changes
  );
  
  // Use virtualized approach to only process visible products
  const paginatedProducts = useMemo(() => {
    // Fast bailout for initial render - return empty array
    if (loadStage < 3) return [];
    
    // Calculate products per page based on mobile/desktop view
    const productsPerPage = isMobile ? ROWS * MOBILE_COLS : PRODUCTS_PER_PAGE;
    
    const start = page * productsPerPage;
    // Use slice for O(1) performance
    return otherProducts.slice(start, start + productsPerPage);
  }, [otherProducts, page, loadStage, isMobile]);

  // Optimized handlers with constant references
  const handlePrev = useCallback(() => setPage(p => Math.max(0, p - 1)), []);
  const handleNext = useCallback(() => setPage(p => Math.min(totalPages - 1, p + 1)), [totalPages]);

  // Touch event handlers for swipe functionality
  const onTouchStart = useCallback((e) => {
    setTouchEnd(null); // Reset touchEnd
    setTouchStart(e.targetTouches[0].clientX);
  }, []);
  
  const onTouchMove = useCallback((e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  }, []);
  
  const onTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isSwipe = Math.abs(distance) > minSwipeDistance;
    
    if (isSwipe) {
      if (distance > 0) {
        // Swiped left, go to next
        if (page < totalPages - 1) {
          handleNext();
          setHasUserSwiped(true);
        }
      } else {
        // Swiped right, go to previous
        if (page > 0) {
          handlePrev();
          setHasUserSwiped(true);
        }
      }
    }
    
    // Reset touch coordinates
    setTouchStart(null);
    setTouchEnd(null);
  }, [touchStart, touchEnd, minSwipeDistance, page, totalPages, handleNext, handlePrev]);

  // Ultra-optimized priority products generation with staged loading
  const priorityProducts = useMemo(() => {
    // Determine number of cards to show based on mobile/desktop view
    const maxCardsToShow = isMobile ? MOBILE_COLS : DESKTOP_COLS;
    
    // Stage 0: Return minimal placeholder array - just enough for layout
    if (loadStage === 0) {
      return Array(Math.min(maxCardsToShow, PRODUCTS_PER_PAGE)).fill({id: 'placeholder'});
    }
    
    // Stage 1-2: Return static placeholders but with IDs to prevent React reconciliation issues
    if (loadStage < 3) {
      return Array(Math.min(maxCardsToShow, PRODUCTS_PER_PAGE))
        .fill(null)
        .map((_, i) => ({id: `placeholder-${i}`}));
    }
    
    // Stage 3+: Return actual products
    return paginatedProducts.slice(0, Math.min(maxCardsToShow, paginatedProducts.length));
  }, [paginatedProducts, loadStage, isMobile]);
  
  // Ultra-optimized progressive loading with intelligent scheduling
  useEffect(() => {
    // Optimize the card element as soon as it's rendered
    if (cardRef.current) {
      optimizeElement(cardRef.current);
    }
    
    let updateId1, updateId2, updateId3;
    
    // Stage 1: Render basic shell - highest priority
    updateId1 = scheduleHighPriorityUpdate(() => {
      setLoadStage(1); // Immediate high priority update
      
      // Stage 2: Render product details - high priority but after stage 1
      updateId2 = scheduleHighPriorityUpdate(() => {
        setLoadStage(2);
        
        // Force a layout calculation to avoid layout thrashing
        if (cardRef.current) {
          forceLayout(cardRef.current);
        }
        
        // Stage 3: Render other products - lower priority
        updateId3 = scheduleLowPriorityUpdate(() => {
          setLoadStage(3);
        });
      });
    });
    
    // Cleanup function
    return () => {
      // Cancel any pending updates
      cancelScheduledUpdate(updateId1, 'high');
      cancelScheduledUpdate(updateId2, 'high');
      cancelScheduledUpdate(updateId3, 'low');
      
      // Clean up performance optimizations
      if (cardRef.current) {
        cleanupElementOptimizations(cardRef.current);
      }
    };
  }, []);

  // Optimized scroll behavior with smooth transitions restored
  useEffect(() => {
    // Only proceed if card is fully loaded
    if (loadStage === 3 && cardRef.current) {
      // Use our optimized high-priority scheduling
      const scrollId = scheduleHighPriorityUpdate(() => {
        // Force any pending layout calculations before scrolling
        forceLayout(cardRef.current);
        
        // Restored smooth scrolling while keeping optimizations
        cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        // Apply GPU acceleration and containment for better performance
        optimizeElement(cardRef.current);
      });
      
      return () => cancelScheduledUpdate(scrollId, 'high');
    }
  }, [loadStage]);

  // Handle cleanup and scroll to product card when closing - highly optimized
  const productIdRef = useRef(product.id);
  
  // Store product ID in ref to avoid dependency changes
  useEffect(() => {
    productIdRef.current = product.id;
  }, [product.id]);
  
  // Optimized unmount effect with smooth scrolling and focus restoration
  useEffect(() => {
    // Keep a reference to any scheduled ID for cleanup
    let scrollBackId;
    
    return () => {
      // When component unmounts (card is closing), find and scroll to the original product
      const productElement = document.getElementById(`product-${productIdRef.current}`);
      
      if (productElement) {
        // Cancel any pending scroll operation
        if (scrollBackId) cancelScheduledUpdate(scrollBackId, 'high');
        
        // Schedule a new high-priority scroll operation
        scrollBackId = scheduleHighPriorityUpdate(() => {
          // Pre-optimize the element for smoother scrolling
          optimizeElement(productElement);
          
          // Prepare the element for proper focus management
          prepareElementForFocus(productElement);
          
          // Always ensure the whole card is in view with some padding
          productElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          
          // Set focus on the original product element to ensure keyboard navigation works
          productElement.focus({ preventScroll: true }); // Prevent additional scrolling
          
          // Clean up optimizations after the animation completes
          setTimeout(() => {
            cleanupElementOptimizations(productElement);
          }, 500); // Increased to allow for smooth scroll to complete
        });
      }
    };
  }, []);
  
  // Optimized video component loading with more aggressive lazy loading
  const videoComponent = useMemo(() => {
    // Only render the video component after the card structure is in place
    if (loadStage < 2) return null;
    
    // Use React.lazy for MobileProductVideo component if it's not already lazy-loaded
    return (
      <MobileProductVideo 
        product={product} 
        canonicalProduct={canonicalProduct} 
        imagePath={product.imagePath} 
        inModal={true} 
        directVideo={true}
        loading="eager" // Keep eager loading
        deferVideoLoading={false} // Keep disabled
        // Add a priority flag to indicate this video should be loaded with high priority
        priority={true}
        // Add a preloadOnly flag for initial fast rendering
        preloadOnly={loadStage < 3}
      />
    );
  }, [product, canonicalProduct, loadStage]);
  
  // Ultra-optimized close animation with multiple safety mechanisms
  const handleClose = useCallback(() => {
    // Start by optimizing the element for animation
    if (cardRef.current) {
      optimizeElement(cardRef.current);
    }
    
    // Start fade out animation
    setAnimation('fadeOut');
    
    // Create a faster, more reliable multi-strategy approach for animation completion
    if (cardRef.current) {
      // Store reference to original product element for focus restoration
      const originalProductElement = document.getElementById(`product-${productIdRef.current}`);
      
      // Prepare the element for focus if it exists
      if (originalProductElement) {
        prepareElementForFocus(originalProductElement);
      }
      
      // Strategy 1: Use animationend event - most accurate
      const animationEndHandler = (event) => {
        // Only trigger if it's our fadeOut animation
        if (event.animationName === 'fadeOut') {
          onClose();
          
          // Focus on the original product element after closing
          if (originalProductElement) {
            setTimeout(() => {
              originalProductElement.focus({ preventScroll: true });
              
              // Ensure the focused element is completely visible to the user
              if (!isElementInViewport(originalProductElement, 40)) {
                // Use 'nearest' instead of 'center' to minimize scrolling when element is already partially visible
                originalProductElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
              }
            }, 50);
          }
        }
      };
      
      cardRef.current.addEventListener('animationend', animationEndHandler, { once: true });
      
      // Strategy 2: Backup timeout - in case animation doesn't trigger event for some reason
      const minimumTimeout = setTimeout(() => {
        onClose();
        
        // Also restore focus after closing
        if (originalProductElement) {
          originalProductElement.focus({ preventScroll: true });
          
          // Ensure the focused element is completely visible to the user
          if (!isElementInViewport(originalProductElement, 40)) {
            originalProductElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }
      }, 300); // Allow enough time for animation to complete
      
      // Clean up timeout when animation completes
      return () => clearTimeout(minimumTimeout);
    } else {
      // If card ref doesn't exist for some reason, still call onClose
      onClose();
    }
  }, [onClose]);

  return (
    <div className={`expanded-product-card ${animation} ${isMobile ? 'mobile-view' : ''}`} ref={cardRef} style={{
      width: '100%',
      padding: 0,
      margin: 0,
      overflow: 'hidden',
      maxWidth: '100%'
    }}>
      <div style={{ 
        maxWidth: '100%', 
        width: '100%', 
        background: '#fff', 
        borderRadius: 12, 
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.16)', 
        margin: 0,
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        overflow: 'hidden',
        position: 'relative',
        // Apply contain for improved performance
        contain: 'content',
        // Optimize for animation
        willChange: 'opacity, transform'
      }}>
        {/* Left: Product details - full width on mobile */}
        <div style={{ 
          width: isMobile ? '100%' : 260, 
          padding: isMobile ? '16px 16px' : '16px 20px', 
          borderRight: isMobile ? 'none' : '1px solid #e6e6e6',
          borderBottom: isMobile ? '1px solid #e6e6e6' : 'none',
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          textAlign: 'center', 
          justifyContent: 'center', 
          position: 'relative' 
        }}>
          {/* Menu type badge (medical/recreational) */}
          {(isMedical || isRecreational) && (
            <div style={{ 
              position: 'absolute', 
              top: 12, 
              left: 12, 
              background: isMedical ? '#33691e' : '#0277bd', 
              color: '#fff', 
              borderRadius: 4, 
              padding: '3px 8px', 
              fontSize: 10, 
              fontWeight: 700, 
              textTransform: 'uppercase',
              letterSpacing: 0.5
            }}>
              {isMedical ? 'MED' : 'REC'}
            </div>
          )}
          <div style={{ 
            width: 110, 
            height: 110, 
            marginBottom: isMobile ? 20 : 30, 
            marginTop: 10, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}>
            {/* Initial placeholder that shows immediately, replaced with video when ready */}
            {loadStage < 2 ? (
              <div className="placeholder-pulse" style={{ 
                width: 100, 
                height: 100, 
                borderRadius: '50%', 
                background: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden'
              }}>
                {loadStage === 1 && (
                  <img 
                    src={product.imagePath} 
                    alt={productDisplayName}
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'contain'
                    }}
                    loading="eager"
                  />
                )}
              </div>
            ) : (
              videoComponent
            )}
          </div>
          {/* Move product details lower on mobile by adding extra margin-top */}
          <h2 style={{ 
            fontSize: isMobile ? 16 : 18, 
            fontWeight: 700, 
            color: '#1a1a1a', 
            textAlign: 'center', 
            margin: isMobile ? '24px 0 2px 0' : '0 0 2px 0', 
            lineHeight: 1.1 
          }}>
            {productDisplayName}
          </h2>
          {(productBrand || productTHC) && (
            <div style={{ 
              fontSize: isMobile ? 11 : 12, 
              color: '#666', 
              marginBottom: 2, 
              fontWeight: 500, 
              textAlign: 'center' 
            }}>
              {productBrand && productTHC ? `${productBrand} • ${productTHC}` : (productBrand || productTHC)}
            </div>
          )}
          <div style={{ 
            fontSize: isMobile ? 14 : 16, 
            fontWeight: 700, 
            marginBottom: 4, 
            textAlign: 'center' 
          }}>
            {onSale ? (
              <>
                <span style={{ color: '#e53935', marginRight: 10 }}>${typeof config.salePrice === 'number' ? (config.salePrice % 1 === 0 ? config.salePrice : config.salePrice.toFixed(2)) : config.salePrice}</span>
                <span style={{ color: '#888', textDecoration: 'line-through', fontSize: isMobile ? 14 : 18 }}>${typeof config.normalPrice === 'number' ? (config.normalPrice % 1 === 0 ? config.normalPrice : config.normalPrice.toFixed(2)) : config.normalPrice}</span>
              </>
            ) : (
              <span style={{ color: '#1a1a1a' }}>${typeof config?.normalPrice === 'number' ? (config.normalPrice % 1 === 0 ? config.normalPrice : config.normalPrice.toFixed(2)) : config?.normalPrice}</span>
            )}
          </div>
          <div className="product-meta" style={{ 
            fontSize: isMobile ? 10 : 11, 
            justifyContent: 'center', 
            display: 'flex', 
            alignItems: 'center', 
            marginBottom: 4,
            flexWrap: isMobile ? 'wrap' : 'nowrap'
          }}>
            <span className={`stock-info ${config?.currentStock <= 0 ? 'out-of-stock' : config?.currentStock <= 5 ? 'low-stock' : ''}`} style={{ 
              display: 'flex', 
              alignItems: 'center',
              margin: isMobile ? '0 4px 4px 0' : '0'
            }}> 
              <span style={{ 
                display: 'inline-block',
                width: 20, 
                height: 20, 
                marginRight: 8,
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23666' stroke='%23666' stroke-width='2' viewBox='0 0 512 512'%3E%3Cpath d='M462.807,128.836,260.141,43.5a10.673,10.673,0,0,0-8.281,0L49.193,128.836a10.67,10.67,0,0,0-6.526,9.831V373.333a10.674,10.674,0,0,0,6.526,9.833L251.859,468.5a10.7,10.7,0,0,0,8.281,0l202.667-85.333a10.674,10.674,0,0,0,6.526-9.833V138.667A10.67,10.67,0,0,0,462.807,128.836ZM256,212.427l-79.895-33.64,174.108-73.308a10.5,10.5,0,0,0,.935-.51l80.03,33.7Zm0-147.521,67.8,28.547L150.526,166.409a10.619,10.619,0,0,0-1.663.907L80.822,138.667ZM64,154.732l181.333,76.351V442.6L64,366.25ZM266.667,442.6V231.083L448,154.732V366.25Z'/%3E%3C/svg%3E")`,
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center'
              }}></span>
              {!config ? (
                'Stock unknown'
              ) : config.currentStock <= 0 ? (
                'Out of Stock'
              ) : config.currentStock <= 5 ? (
                `${config.currentStock} left`
              ) : (
                `${config.currentStock > 20 ? '20+' : config.currentStock} left`
              )}
            </span>
            {isMobile ? null : <span className="separator" style={{ margin: '0 12px', color: '#999', fontWeight: 400 }}>|</span>}
            <span className="distance-info" style={{ 
              display: 'flex', 
              alignItems: 'center',
              margin: isMobile ? '0 0 4px 4px' : '0'
            }}>
              <span style={{ 
                display: 'inline-block',
                width: 20, 
                height: 20, 
                marginRight: 8,
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23666' viewBox='0 0 24 24'%3E%3Cpath d='M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22S19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9S10.62 6.5 12 6.5S14.5 7.62 14.5 9S13.38 11.5 12 11.5Z'/%3E%3C/svg%3E")`,
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center'
              }}></span>
              {typeof store.distance === 'number' && isFinite(store.distance) ? (
                <>{store.distance.toFixed(1)} mi</>
              ) : (
                store.city && store.state ? <>{store.city}, {store.state}</> : ''
              )}
            </span>
          </div>
          {hasValidUrl ? (
            <button
              className="view-details-button"
              aria-label={`View ${productDisplayName} product`}
              onClick={() => {
                if (hasValidUrl && config?.currentStock > 0) {
                  window.open(productUrl, '_blank', 'noopener,noreferrer');
                }
              }}
              disabled={!hasValidUrl || config?.currentStock <= 0}
            >
              View product
            </button>
          ) : (
            <button
              className="view-details-button disabled"
              disabled
            >
              Link unavailable
            </button>
          )}
          
          {/* Close button for mobile view - always visible at top right */}
          {isMobile && (
            <button
              className="close-expanded-card-mobile"
              onClick={handleClose}
              style={{ 
                background: 'none', 
                border: 'none', 
                fontSize: 28, 
                cursor: 'pointer', 
                color: '#666', 
                position: 'fixed',
                top: 16,
                right: 16,
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                borderRadius: '50%'
              }}
              title="Close"
            >
              ×
            </button>
          )}
        </div>
        
        {/* Only show separator on desktop */}
        {!isMobile && (
          <div style={{ 
            width: 1, 
            background: '#e0e0e0', 
            margin: '8px 0', 
            alignSelf: 'stretch', 
            position: 'relative', 
            zIndex: 2 
          }} />
        )}
        
        {/* Right: Other products - full width on mobile */}
        <div style={{ 
          flex: 1, 
          padding: isMobile ? '16px 12px' : '16px 12px', 
          display: 'flex', 
          flexDirection: 'column', 
          height: '100%', 
          position: 'relative' 
        }}>
          {/* Close button positioned absolutely to keep it in place - desktop only */}
          {!isMobile && (
            <button
              className="close-expanded-card"
              onClick={handleClose}
              style={{ 
                background: 'none', 
                border: 'none', 
                fontSize: 24, 
                cursor: 'pointer', 
                color: '#666', 
                position: 'absolute',
                top: 16,
                right: 12
              }}
              title="Close"
            >
              ×
            </button>
          )}
          
          {/* Other products heading with increased top margin */}
          <h4 style={{ 
            margin: isMobile ? '0 0 16px 0' : '42px 0 16px 0', 
            fontSize: 14, 
            color: '#333', 
            fontWeight: 600,
            textAlign: isMobile ? 'center' : 'left'
          }}>
            Other Products at {store.name}
          </h4>
          
          {/* Product grid with swipe functionality */}
          <div 
            ref={swipeAreaRef}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            style={{ 
              cursor: isMobile ? 'grab' : 'default',
              userSelect: 'none',
              position: 'relative'
            }}
          >
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', 
              gridTemplateRows: isMobile ? 'repeat(2, auto)' : 'auto',
              gap: isMobile ? 8 : 6, 
              marginTop: 8 
            }}>
                {priorityProducts.map((otherProduct, index) => 
                  otherProduct.id === 'placeholder' ? (
                    <PlaceholderCard key={`placeholder-${index}`} />
                  ) : (
                    <MemoizedModalProductCard
                      key={otherProduct.id}
                      product={otherProduct}
                      config={otherProduct.configs?.[0]}
                      canonicalProduct={otherProduct.canonicalProduct}
                      onSale={otherProduct.onSale}
                      hasValidUrl={otherProduct.hasValidUrl}
                      small={true}
                      compact={isMobile ? 'mobile' : true}
                      distance={distance}
                      city={city}
                      state={state}
                      onViewDetails={otherProduct.onViewDetails}
                    />
                  )
                )}
              </div>
              
              {/* Swipe hint for mobile users - shows only if user hasn't swiped yet */}
              {isMobile && !hasUserSwiped && totalPages > 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginTop: 12,
                  opacity: 0.6,
                  fontSize: 12,
                  color: '#888',
                  gap: 4
                }}>
                  <span>⟪⟪</span>
                  <span>Swipe to navigate</span>
                  <span>⟫⟫</span>
                </div>
              )}
              
              {totalPages > 1 && (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  marginTop: isMobile ? (hasUserSwiped ? 16 : 8) : 16, 
                  gap: 6 
                }}>
                  <button
                    onClick={handlePrev}
                    disabled={page === 0}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 3,
                      padding: isMobile ? '5px 14px' : '4px 12px',
                      borderRadius: 8,
                      border: 'none',
                      background: page === 0 ? '#f3f4f6' : '#1976d2',
                      color: page === 0 ? '#bbb' : '#fff',
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: page === 0 ? 'not-allowed' : 'pointer',
                      boxShadow: page === 0 ? 'none' : '0 1px 4px rgba(25, 118, 210, 0.08)',
                      minWidth: isMobile ? 80 : 'auto'
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 18 18" fill="none" style={{ marginRight: 2 }}><path d="M11.5 14L6.5 9L11.5 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    Prev
                  </button>
                  <span style={{ 
                    fontSize: 12, 
                    color: '#666', 
                    fontWeight: 500, 
                    minWidth: 60, 
                    textAlign: 'center' 
                  }}>
                    {isMobile ? `${page + 1}/${totalPages}` : `Row ${page + 1} of ${totalPages}`}
                  </span>
                  <button
                    onClick={handleNext}
                    disabled={page === totalPages - 1}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 3,
                      padding: isMobile ? '5px 14px' : '4px 12px',
                      borderRadius: 8,
                      border: 'none',
                      background: page === totalPages - 1 ? '#f3f4f6' : '#1976d2',
                      color: page === totalPages - 1 ? '#bbb' : '#fff',
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: page === totalPages - 1 ? 'not-allowed' : 'pointer',
                      boxShadow: page === totalPages - 1 ? 'none' : '0 1px 4px rgba(25, 118, 210, 0.08)',
                      minWidth: isMobile ? 80 : 'auto'
                    }}
                  >
                    Next
                    <svg width="16" height="16" viewBox="0 0 18 18" fill="none" style={{ marginLeft: 2 }}><path d="M6.5 14L11.5 9L6.5 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                </div>
              )}
            </div>
          </div>
      </div>
    </div>
  );
};

ExpandedProductCard.propTypes = {
  product: PropTypes.object.isRequired,
  store: PropTypes.object.isRequired,
  config: PropTypes.object,
  canonicalProduct: PropTypes.object,
  onSale: PropTypes.bool,
  hasValidUrl: PropTypes.bool,
  productUrl: PropTypes.string,
  otherProducts: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  distance: PropTypes.number,
  city: PropTypes.string,
  state: PropTypes.string
};

// Export a memoized version of the card to prevent unnecessary re-renders from parent components
export default memo(ExpandedProductCard);
